import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router'
import { Alert, Button, Spinner } from 'react-bootstrap'
import { getFootballUrlParams, teamUrl, nflTeamImage } from '@severed-links/common.redherrings-constants'
import moment from 'moment-timezone'
import Line from '../Line'
import useMatchup from './useMatchup'
import { gameStatus, teamScore, pickDetails } from './matchupPresentation'
import * as s from './MatchUp.scss'

const PoolTeam = ({ team, scoreboard, visible }) => {
  const details = teamScore(scoreboard, team)
  return <Link className={s.poolTeam} to={`/football/team-profile/${team._id}`}>
    <img src={teamUrl(team.teamAvatarUrl)} alt='' />
    <div className={s.poolName}>
      <strong>{team.teamName || 'Team unavailable'}</strong>
      <small>{details.record || 'Record unavailable'}</small>
      {typeof details.chance === 'number' ? <small>{(details.chance * 100).toFixed(1)}% chance to win</small> : null}
    </div>
    <div className={s.poolScore}><strong>{details.score ?? '—'}</strong></div>
  </Link>
}

const NflSide = ({ game, favorite }) => {
  const prefix = favorite ? 'favorite' : 'underdog'
  const home = favorite ? game.isHomeFavorite : !game.isHomeFavorite
  return <div className={`${s.nflSide} ${!favorite ? s.underdog : ''}`}>
    <div className={s.nflIdentity}>
      <img src={nflTeamImage(game[`${prefix}Mascot`])} alt='' />
      <div className={s.nflName} title={game[`${prefix}Mascot`]}>
        <strong className={s.fullName}>{game[`${prefix}Mascot`]}</strong>
        <strong className={s.abbreviation}>{(game[`${prefix}Abbrev`] || '').toUpperCase()}</strong>
        <small>{home ? 'Home' : 'Away'}<span className={s.nflRecord}>{game[`${prefix}Record`] ? ` · ${game[`${prefix}Record`]}` : ''}</span></small>
      </div>
    </div>
    {game[`${prefix}Score`] != null ? <strong className={s.nflScore}>{game[`${prefix}Score`]}</strong> : null}
  </div>
}

const MatchUp = () => {
  const params = useParams()
  const seasons = useSelector(state => state.football.seasons) || []
  const context = getFootballUrlParams(params, seasons)
  const { matchup, scoreboard, loading, error, refresh } = useMatchup(context)
  const [filter, setFilter] = useState('All')
  const teams = [matchup?.team1, matchup?.team2]
  const games = matchup?.nflGames || []
  const visibleGames = games.filter(game => filter === 'All' || (filter === 'Live' ? game.isInProgress && !game.isComplete : game.isComplete))
  return <div className={s.container}>
    <header className={s.heading}>
      <div><p>{context.seasonName} · Regular season</p><h1>Week {context.week} matchup</h1></div>
      <div className={s.actions}>
        <Link to={`/football/regular-season/${context.seasonName}`}>Season overview</Link>
        <Button variant='outline-success' disabled={loading} onClick={() => refresh()}>{loading ? <Spinner size='sm' /> : null} {loading ? 'Refreshing…' : 'Refresh'}</Button>
      </div>
    </header>
    {error ? <Alert variant='danger' role='alert'>{error} {matchup ? 'Showing the last available data.' : ''} <Button variant='link' disabled={loading} onClick={() => refresh()}>Try again</Button></Alert> : null}
    {!matchup && !error ? <p className={s.empty} role='status'>Loading matchup…</p> : null}
    {matchup && teams.every(team => team?.teamName) ? <>
      <section className={s.summary} aria-label='Pool scoreboard'>
        {teams.map((team, index) => <PoolTeam team={team} scoreboard={scoreboard} visible={matchup[`showTeam${index + 1}`] === true} key={team._id} />)}
      </section>
      {typeof scoreboard.cot === 'number' && scoreboard.cot > 0 ? <p className={s.note}>{(scoreboard.cot * 100).toFixed(1)}% chance of a tie</p> : null}
      <div className={s.gameToolbar}>
        <div><h2>NFL games & picks</h2><p>Favorite on the left · Underdog on the right · Avatars follow the team picked. Blue: upcoming · Light green/red: live · Dark green/red: final.</p></div>
        <div className={s.filters} role='group' aria-label='Filter NFL games'>
          {['All', 'Live', 'Final'].map(value => <button type='button' aria-pressed={filter === value} onClick={() => setFilter(value)} key={value}>{value}</button>)}
        </div>
      </div>
      {teams.map((team, index) => matchup[`showTeam${index + 1}`] !== true ? <p className={s.visibilityNote} key={team._id}>{team.teamName} picks are hidden until after the pick deadline.</p> : null)}
      {visibleGames.map((game, index) => <React.Fragment key={game.game}>
        {index === 0 || !moment(game.date).isSame(visibleGames[index - 1].date, 'day') ? <h3 className={s.dateHeading}>{moment(game.date).format('ddd, MMM D')}</h3> : null}
        <article className={s.game} aria-label={`${game.favoriteMascot} versus ${game.underdogMascot}`}>
          <div className={s.gameStatus}>{gameStatus(game)}</div>
          <div className={s.nflRow}>
            {['favorite', 'underdog'].map((side, sideIndex) => <React.Fragment key={side}>
              {sideIndex === 1 ? <div className={s.spread}><small>Spread</small><strong><Line line={game.line} showSign={false} /></strong></div> : null}
              <div className={`${s.gameSide} ${sideIndex === 1 ? s.rightSide : ''}`}>
                <NflSide game={game} favorite={sideIndex === 0} />
                <div className={s.pickSide}>
                  {teams.map((team, teamIndex) => {
                    const pick = pickDetails(game, team, matchup[`showTeam${teamIndex + 1}`] === true)
                    const label = `${team.teamName}: ${pick.text}${pick.outcome ? ` · ${pick.outcome}` : ''}`
                    return pick.side === side ? <div className={`${s.pick} ${s[pick.tone]}`} key={team._id} title={label} aria-label={label}>
                      <img src={teamUrl(team.teamAvatarUrl)} alt='' />
                      <span className={s.pickName}>{team.teamName}</span>
                      <strong>{pick.rank}</strong>
                    </div> : null
                  })}
                </div>
              </div>
            </React.Fragment>)}
          </div>
        </article>
      </React.Fragment>)}
      {!visibleGames.length ? <p className={s.empty}>{filter === 'All' ? 'No NFL games are available yet.' : `No ${filter.toLowerCase()} games to show.`}</p> : null}
    </> : matchup ? <p className={s.empty}>This matchup is not available yet.</p> : null}
  </div>
}

export default MatchUp
